import { PropsWithChildren, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { INTERVAL_MAX_METERS_DISPLAY_COUNT } from '@/utils/constants'

type IntervalTooltipWrapperProps = PropsWithChildren & {
    label: string
    tooltipItemsWrapperClassname?: string
    dataCompleteness?: {
        metersWithData: number
        totalMeters: number
    }
    selectedMeters?: number[]
    meters?: Array<{ meterId: number; meterName: string }>
    dataGrouping?: 'aggregate' | 'compare'
    display?: 'meters' | 'subMeters'
}

export const IntervalTooltipWrapper: React.FC<IntervalTooltipWrapperProps> = (
    props
) => {
    const {
        label,
        children,
        tooltipItemsWrapperClassname,
        dataCompleteness,
        selectedMeters = [],
        meters,
        dataGrouping,
        display = 'meters',
    } = props
    const { t } = useTranslation('translations')
    const defaultWrapperClassname =
        'grid grid-cols-[1fr,auto,auto] items-center overflow-hidden gap-x-2 gap-y-1 z-[50]'
    const wrapperClassname =
        tooltipItemsWrapperClassname ?? defaultWrapperClassname

    const percentage =
        dataCompleteness && dataCompleteness.totalMeters > 0
            ? Math.round(
                  (dataCompleteness.metersWithData /
                      dataCompleteness.totalMeters) *
                      100
              )
            : 0

    const showMetersAlert = useMemo(() => {
        // Don't show alert when data grouping is aggregate (backend doesn't limit meters)
        if (dataGrouping === 'aggregate') {
            return false
        }

        if (selectedMeters.length > INTERVAL_MAX_METERS_DISPLAY_COUNT) {
            return true
        }

        if (
            selectedMeters.length === 0 &&
            meters &&
            meters.length > INTERVAL_MAX_METERS_DISPLAY_COUNT
        ) {
            return true
        }

        return false
    }, [selectedMeters.length, meters, dataGrouping])

    return (
        <div className="relative rounded-lg border border-neutral-200 bg-white px-3 py-2 shadow-sm">
            <div className="space-y-2">
                <div className="text-heading-7">{label}</div>
                <div className={wrapperClassname}>{children}</div>
                {dataCompleteness && (
                    <>
                        <div className="border-t border-neutral-200" />
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-label-xsmall text-subtle">
                                {t(
                                    'active_management.interval.chart_tooltip.data_completeness'
                                )}
                            </span>
                            <div className="flex items-center gap-2">
                                <span className="text-label-xsmall font-bold text-subtle">
                                    {dataCompleteness.metersWithData}/
                                    {dataCompleteness.totalMeters}
                                </span>
                                <span className="text-label-xsmall inline-flex min-w-6 items-center justify-center gap-0.5 rounded-[6px] border border-neutral-300 px-1 py-[2px] text-regular">
                                    {percentage}%
                                </span>
                            </div>
                        </div>
                    </>
                )}
                {showMetersAlert && (
                    <div className="rounded-[6px] border border-blue-200 bg-blue-50 px-2 py-1.5">
                        <span className="text-label-xsmall text-regular">
                            {t(
                                'active_management.interval.chart_tooltip.tooltip_alert',
                                {
                                    count: INTERVAL_MAX_METERS_DISPLAY_COUNT,
                                    type:
                                        display === 'subMeters'
                                            ? 'submeters'
                                            : 'meters',
                                    filterType:
                                        display === 'subMeters'
                                            ? 'Submeter'
                                            : 'Meter',
                                }
                            )}
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}
